import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { getCommentKey } from '../../discussions/utils/commentUtil';
import {
  fetchCommentUpvoteStatus,
  resetCommentUpvotes,
  toggleUpvoteComment,
} from '../actions/commentUpvoteActions';

const initialState: CommentUpvoteState = {
  byBlogDiscussionAndComment: {},
};

const commentUpvoteSlice = createSlice({
  name: 'commentUpvote',
  initialState,
  reducers: {},
  extraReducers: builder => {
    resetCommentUpvotesHandler(builder);
    getCommentUpvoteStatus(builder);
    upvoteComment(builder);
  },
});

// Status Fetch Handlers

const resetCommentUpvotesHandler = (
  builder: ActionReducerMapBuilder<CommentUpvoteState>
) => {
  builder.addCase(resetCommentUpvotes, state => {
    state.byBlogDiscussionAndComment = {};
  });
};

const getCommentUpvoteStatus = (
  builder: ActionReducerMapBuilder<CommentUpvoteState>
) => {
  builder
    .addCase(fetchCommentUpvoteStatus.pending, (state, action) => {
      const { contentId, discussionId, commentId } = action.meta.arg;
      const key = getCommentKey(contentId, discussionId, commentId);

      if (state.byBlogDiscussionAndComment[key]) {
        // Preserve existing values so the UI doesn't flash while fetching
        state.byBlogDiscussionAndComment[key].loading = true;
        state.byBlogDiscussionAndComment[key].error = null;
      } else {
        state.byBlogDiscussionAndComment[key] = {
          contentId,
          discussionId,
          commentId,
          total_upvotes: 0,
          user_has_upvoted: false,
          loading: true,
          error: null,
        };
      }
    })
    .addCase(fetchCommentUpvoteStatus.fulfilled, (state, action) => {
      const { commentId, data } = action.payload;
      const key = getCommentKey(data.contentId, data.discussionId, commentId);

      state.byBlogDiscussionAndComment[key] = {
        ...data,
        loading: false,
        error: null,
      };
    })
    .addCase(fetchCommentUpvoteStatus.rejected, (state, action) => {
      const { contentId, discussionId, commentId } = action.meta.arg;
      const key = getCommentKey(contentId, discussionId, commentId);

      if (state.byBlogDiscussionAndComment[key]) {
        state.byBlogDiscussionAndComment[key].loading = false;
        state.byBlogDiscussionAndComment[key].error =
          action.error.message || 'Failed to fetch comment upvotes';
      } else {
        state.byBlogDiscussionAndComment[key] = {
          contentId,
          discussionId,
          commentId,
          total_upvotes: 0,
          user_has_upvoted: false,
          loading: false,
          error: action.error.message || 'Failed to fetch comment upvotes',
        };
      }
    });
};

// Upvote Toggle Handlers
const upvoteComment = (
  builder: ActionReducerMapBuilder<CommentUpvoteState>
) => {
  builder
    .addCase(toggleUpvoteComment.pending, (state, action) => {
      const { contentId, discussionId, commentId } = action.meta.arg;
      const key = getCommentKey(contentId, discussionId, commentId);

      if (!state.byBlogDiscussionAndComment[key]) {
        state.byBlogDiscussionAndComment[key] = {
          contentId,
          discussionId,
          commentId,
          total_upvotes: 0,
          user_has_upvoted: false,
          loading: false,
          error: null,
        };
      }
      // Optimistic update: flip immediately
      const entry = state.byBlogDiscussionAndComment[key];
      entry.user_has_upvoted = !entry.user_has_upvoted;
      entry.total_upvotes = entry.user_has_upvoted
        ? entry.total_upvotes + 1
        : entry.total_upvotes - 1;
      entry.loading = true;
      entry.error = null;
    })
    .addCase(toggleUpvoteComment.fulfilled, (state, action) => {
      const { contentId, discussionId, commentId } = action.meta.arg;
      const key = getCommentKey(contentId, discussionId, commentId);
      const { total_upvotes, user_has_upvoted } = action.payload.data;

      if (state.byBlogDiscussionAndComment[key]) {
        state.byBlogDiscussionAndComment[key].total_upvotes = total_upvotes;
        state.byBlogDiscussionAndComment[key].user_has_upvoted = user_has_upvoted;
        state.byBlogDiscussionAndComment[key].loading = false;
      }
    })
    .addCase(toggleUpvoteComment.rejected, (state, action) => {
      const { contentId, discussionId, commentId } = action.meta.arg;
      const key = getCommentKey(contentId, discussionId, commentId);

      if (state.byBlogDiscussionAndComment[key]) {
        // Revert optimistic update
        const entry = state.byBlogDiscussionAndComment[key];
        entry.user_has_upvoted = !entry.user_has_upvoted;
        entry.total_upvotes = entry.user_has_upvoted
          ? entry.total_upvotes + 1
          : entry.total_upvotes - 1;
        entry.loading = false;
        entry.error = action.error.message || 'Failed to toggle comment upvote';
      }
    });
};

export default commentUpvoteSlice.reducer;
